import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import { MIN_TEETH_SCALE, teethScale } from "./chart-fit";
import type { ChartMeasure } from "./chart-fit";
import styles from "./tooth-chart.module.css";
import {
  CHART_STATE_LABEL,
  GLYPH_LABEL,
  TEETH_LOWER,
  TEETH_UPPER,
  canalPath,
  columnWidth,
  crownCapFade,
  crownCapTransform,
  isPrimaryTooth,
  primaryToothBox,
  tiersOf,
  toothName,
  toothShape,
  waitingBehind,
} from "./tooth-geometry";
import type { ChartToothStateName, ToothGlyphName } from "./tooth-geometry";
import { SurfaceSquare } from "./tooth-surfaces";

/**
 * The odontogram: both arches, all 32 permanent teeth, FDI numbering — and,
 * with `primaryTeeth` on, the twenty baby teeth too (BT1–BT8).
 *
 * **Rendering only.** Recording work against a tooth is STORY-022; this draws
 * the chart and reports which tooth was clicked, and that separation is why the
 * geometry lives in a pure module beside it.
 *
 * Two straight rows of anatomical silhouettes, crowns meeting at the occlusal
 * line — the client's direction of 2026-09-19, replacing the mockup's rotated
 * horseshoe. `tooth-geometry.ts` carries the reasoning and the decision id.
 *
 * Each tooth is a real `<button>` wrapping its own `<svg>`, rather than a
 * `<g>` inside one large one. A button is focusable, labellable and pressable
 * without any of it being re-implemented, and the row is then ordinary flex
 * layout instead of thirty-two absolute positions.
 */

export interface ToothChartProps {
  /**
   * State per FDI number. A tooth absent from this map renders sound, because
   * an unrecorded tooth **has no row** — the chart draws the absence of a
   * record, not a record of absence.
   */
  states?: Partial<Record<number, ChartToothStateName>>;
  /**
   * Glyphs per FDI number (UI-TC10) — the crown mark and the root-canal line.
   *
   * Separate from `states` because a glyph is **additive**: it says what kind
   * of work is on the tooth, never whether it is outstanding. A tooth can carry
   * both, in any state.
   */
  glyphs?: Partial<Record<number, readonly ToothGlyphName[]>>;
  /**
   * **The parts of each tooth that have been worked on**, from M, O, D, B and
   * L. Given, every tooth draws its top view in front of it — between the
   * tooth and the bite line — with those parts blue. Absent, as in the
   * booking form's picker, no top view is drawn.
   */
  surfaces?: Partial<Record<number, readonly string[]>>;
  /** The parts planned work names, drawn red on the same top views. */
  plannedSurfaces?: Partial<Record<number, readonly string[]>>;
  selectedTooth?: number | null;
  /**
   * Several teeth chosen at once — the booking form's picker (BF6). Drawn with
   * the same heavier outline as `selectedTooth`; either marks a tooth chosen.
   */
  selectedTeeth?: readonly number[];
  onSelectTooth?: (fdiNumber: number) => void;
  /**
   * Fill the height it is given, drawing the teeth larger to use it (PF7) —
   * the patient file. Off, the chart is its natural size, as the booking
   * form's picker draws it.
   */
  fit?: boolean;
  /**
   * With `fit`, told what the chart needs (PF13): its rows' height at zoom 1
   * and the rest of its card — padding, legend, borders. The patient file
   * sizes the cards under the chart from it, so the teeth get the room first.
   */
  onMeasure?: (needs: ChartNeeds) => void;
  /**
   * **Draw the baby teeth** (BT1–BT8). Each arch then has two tiers: next to
   * the bite, the baby teeth and the adult molars already in the mouth;
   * further out, in the same column, the adult tooth that will replace each
   * baby tooth — faded and not clickable until that baby tooth is marked
   * fallen (MISSING). Off, the chart is the 32 adult teeth it always was.
   */
  primaryTeeth?: boolean;
  /**
   * The *Baby teeth* switch, drawn beside the legend when given (BT8). The
   * patient file passes it; the booking form's picker follows the patient's
   * setting and does not.
   */
  onPrimaryTeethChange?: (shown: boolean) => void;
  /**
   * **Says an adult tooth has come in** (BT10): double-clicking a faded adult
   * tooth calls this with its number, and the patient file marks the baby tooth
   * in front of it fallen. Given, a waiting tooth can also be clicked, to open
   * it. Absent, as in the booking form's picker, a waiting tooth stays disabled,
   * because there is nothing to book on a tooth that has not come in (BT6).
   */
  onReplacePrimary?: (fdiNumber: number) => void;
  /** Buttons drawn in the footer, before the *Baby teeth* switch — the patient file's popups. */
  actions?: React.ReactNode;
  /**
   * The quick ring's name for a tooth here (STORY-221) — `"file.tooth"` in the
   * patient file. Absent, as in the booking form's picker, a tooth is no thing
   * of its own to the ring.
   */
  ringKind?: string;
}

/** What the chart needs from the space it is given — see `onMeasure`. */
export type ChartNeeds = ChartMeasure;

const STATE_CLASS: Record<ChartToothStateName, string> = {
  SOUND: "",
  TO_BE_DONE: styles.toBeDone!,
  DONE: styles.done!,
  MISSING: styles.missing!,
};

/**
 * The legend's four rows (BF5). *Done* reads the same for everyone — it split
 * into "done by me" and "done by others" under UI-TC7, and Anthony reversed it.
 */
const LEGEND: { state: ChartToothStateName; label: string }[] = [
  { state: "SOUND", label: "Not worked on" },
  { state: "TO_BE_DONE", label: "To be done" },
  { state: "DONE", label: "Done" },
  { state: "MISSING", label: "Removed / missing" },
];

/** On a chart with baby teeth, MISSING is also how a baby tooth is marked fallen (BT7). */
const MISSING_WITH_PRIMARY = "Removed / missing / fallen";

/**
 * The cross-hatch a removed tooth is filled with.
 *
 * It has to be an SVG `<pattern>` now that the tooth is a path rather than a
 * box — `repeating-linear-gradient` paints a rectangle, and a rectangle behind
 * a tooth-shaped hole is not a hatched tooth. Declared once for the whole
 * chart; the chart renders once per page.
 *
 * This is the channel that survives a greyscale print, a colour-vision
 * deficiency and a squint all at once, which is why a removed tooth carries it
 * rather than simply being paler.
 */
const HATCH_ID = "tooth-chart-hatch";

function ChartDefs(): React.JSX.Element {
  return (
    <svg className={styles.defs} aria-hidden="true" focusable="false">
      <defs>
        <pattern
          id={HATCH_ID}
          width="4.5"
          height="4.5"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(135)"
        >
          {/* The 135° spacing is the mockup's own cross-hatch, kept. */}
          <line x1="0" y1="0" x2="0" y2="4.5" className={styles.hatchLine} strokeWidth="1" />
        </pattern>
      </defs>
    </svg>
  );
}

export function ToothChart({
  states = {},
  glyphs = {},
  surfaces,
  plannedSurfaces = {},
  selectedTooth = null,
  selectedTeeth = [],
  onSelectTooth,
  fit = false,
  onMeasure,
  actions,
  primaryTeeth = false,
  onPrimaryTeethChange,
  onReplacePrimary,
  ringKind,
}: ToothChartProps): React.JSX.Element {
  // The baby-teeth layer adds a tier to each arch, so the rows have a
  // different natural size with it on — the fit has to measure them again.
  const scale = useFitScale(fit, primaryTeeth ? "with-baby-teeth" : "adult", onMeasure);
  const toothRefs = useSlideIntoPlace(states, primaryTeeth, scale.rowsRef);

  const isChosen = (fdiNumber: number): boolean =>
    fdiNumber === selectedTooth || selectedTeeth.includes(fdiNumber);

  /**
   * **An adult tooth still behind its baby tooth** (BT6, BT9): drawn faded
   * while the baby tooth in its place has not been marked fallen. The API
   * refuses work on it as well, so this is the affordance, not the rule.
   */
  const isWaiting = (fdiNumber: number): boolean =>
    waitingBehind(fdiNumber, states, primaryTeeth) !== null;

  const renderTooth = (fdiNumber: number, isUpper: boolean): React.JSX.Element => {
    const shape = toothShape(fdiNumber, isUpper);
    // A baby tooth is its type's adult silhouette drawn smaller (BT8): the
    // viewBox stays the adult one, so the canal and crown marks still fit.
    const isBaby = isPrimaryTooth(fdiNumber);
    const box = isBaby ? primaryToothBox(shape) : shape;
    const waiting = isWaiting(fdiNumber);
    const state = states[fdiNumber] ?? "SOUND";
    /**
     * **A fallen baby tooth, kept for reference** (QA4-D10): with the layer on,
     * a baby tooth marked fallen sits faded in the outer row where its adult
     * tooth waited, so what was done on it is a click away. Hatched already, as
     * MISSING is; faded like a waiting tooth. Nothing can be booked on it.
     */
    const fallen = primaryTeeth && isBaby && state === "MISSING";
    const marks = glyphs[fdiNumber] ?? [];
    const hasCanal = marks.includes("ROOT_CANAL");
    const hasCrown = marks.includes("CROWN");
    const canal = canalPath(shape, isUpper);
    // One clip per tooth, because the crown box differs with the tooth's
    // shape and its arch. FDI numbers are unique within a chart.
    // One gradient per tooth, because the fade runs between this tooth's
    // own biting edge and its own neck. FDI numbers are unique in a chart.
    const capFadeId = `tooth-crown-fade-${fdiNumber}`;
    const capMaskId = `tooth-crown-mask-${fdiNumber}`;
    const capFade = crownCapFade(shape, isUpper);
    const capTransform = crownCapTransform(shape, isUpper);

    // The drawing box travels as custom properties rather than as a style
    // soup, so the stylesheet still owns every rule and the inline part is
    // nothing but the numbers that genuinely differ per tooth.
    //
    // `--tooth-col` is the column, `--tooth-w` the silhouette inside it
    // (UI-TC6). They differ wherever the two arches disagree on width — a
    // lower incisor is 17px in a 22px column — and that gap is what puts
    // 18 directly above 48 while leaving each tooth its real proportions.
    const style = {
      "--tooth-col": `${String(columnWidth(fdiNumber))}px`,
      "--tooth-w": `${String(box.width)}px`,
      "--tooth-h": `${String(box.height)}px`,
    } as CSSProperties;

    const predecessor = waitingBehind(fdiNumber, states, primaryTeeth);
    const worked = surfaces?.[fdiNumber] ?? [];
    const toDo = plannedSurfaces[fdiNumber] ?? [];

    /**
     * **The tooth from above**, in front of it — under an upper tooth, over a
     * lower one, so the parts face the bite line as the crowns do. Its slot is
     * kept on a removed tooth, so the crowns stay on one line, but nothing is
     * drawn in it: a tooth that is not there has no surfaces.
     */
    const surfaceSlot =
      surfaces === undefined ? null : (
        <span className={styles.surfaceSlot} data-testid="tooth-surfaces">
          {state !== "MISSING" && (
            <SurfaceSquare
              fdiNumber={fdiNumber}
              worked={worked}
              planned={toDo}
              className={styles.surfaceSquare}
            />
          )}
        </span>
      );

    return (
      <button
        key={fdiNumber}
        ref={(element) => {
          if (element === null) toothRefs.current.delete(fdiNumber);
          else toothRefs.current.set(fdiNumber, element);
        }}
        type="button"
        style={style}
        className={`${styles.tooth} ${surfaces === undefined ? "" : styles.withSurfaces} ${STATE_CLASS[state]} ${
          isChosen(fdiNumber) ? styles.selected : ""
        } ${waiting || fallen ? styles.waiting : ""}`}
        // Not pressable where there is nothing to do to a tooth that has not
        // come in (BT6): the booking picker. In the patient file it opens, and
        // a double-click says it has come in (BT10).
        disabled={(waiting || fallen) && onReplacePrimary === undefined}
        // The anatomical name, because "18" read aloud is a number and
        // "Upper right third molar" is a tooth.
        aria-label={[
          toothName(fdiNumber),
          waiting && predecessor !== null
            ? `not come in yet, baby tooth ${String(predecessor)} still there`
            : fallen
              ? "fallen, kept for its history"
              : CHART_STATE_LABEL[state],
          ...marks.map((mark) => GLYPH_LABEL[mark]),
          ...(worked.length > 0 ? [`surfaces worked on: ${worked.join("")}`] : []),
          ...(toDo.length > 0 ? [`surfaces to be done: ${toDo.join("")}`] : []),
        ].join(", ")}
        aria-pressed={isChosen(fdiNumber)}
        onClick={(event) => {
          // The second click of a double-click. The first already chose the
          // tooth, and toggling it off here would close it mid-gesture.
          if (event.detail > 1) return;
          onSelectTooth?.(fdiNumber);
        }}
        onDoubleClick={() => {
          if (waiting && onReplacePrimary !== undefined) onReplacePrimary(fdiNumber);
        }}
        data-tooth={fdiNumber}
        data-state={state}
        data-type={shape.key}
        data-primary={isBaby ? "" : undefined}
        data-waiting={waiting ? "" : undefined}
        data-fallen={fallen ? "" : undefined}
        data-ring={ringKind}
        data-card=""
        data-testid="tooth"
      >
        {!isUpper && surfaceSlot}
        <svg
          className={styles.glyph}
          viewBox={`0 0 ${shape.width} ${shape.height}`}
          // Stretched to the baby box; an adult tooth's box is its viewBox.
          preserveAspectRatio={isBaby ? "none" : undefined}
          aria-hidden="true"
          focusable="false"
        >
          {/*
            Two paths, same outline. The first is the tooth's fill and
            border; the second carries the cross-hatch and is empty in
            every state but MISSING. Painting both from one path would
            mean the hatch replacing the fill rather than sitting on it.
          */}
          <path className={styles.body} d={shape.path} />
          <path className={styles.hatch} d={shape.path} fill={`url(#${HATCH_ID})`} />
          {/*
            The procedure marks, last so they sit above the fill, the hatch
            and the neck. Additive: neither changes the tooth's colour, and
            a tooth carrying both is an ordinary sequence — a root canal
            with a crown over it.

            The canal system (UI-TC14) — a pulp chamber and one channel per
            root, which is what a clinician looks at. Drawn TWICE from the
            same path: a near-black casing underneath and the white filling
            on top (UI-TC17), the arrangement a radiograph shows.
            The casing is not decoration. White alone is 1.5:1 on a sound
            tooth and 1.1:1 on a to-be-done one — invisible on three of the
            five states. Do not collapse these into one stroke.
          */}
          {hasCanal && (
            <>
              <path className={styles.canalCasing} d={canal} />
              <path className={styles.canalCore} d={canal} />
            </>
          )}
          {/*
            The crown (UI-TC15) — a cap fitted over the tooth down to the
            margin at the neck, rather than the badge-like glyph UI-TC10
            drew inside it. It is the silhouette stroked and clipped to the
            crown half, so every shape gets a correctly-fitted cap and a
            re-cut tooth carries its own.
          */}
          {hasCrown && (
            <>
              <linearGradient
                id={capFadeId}
                gradientUnits="userSpaceOnUse"
                x1="0"
                y1={capFade.fromY}
                x2="0"
                y2={capFade.toY}
              >
                {/*
                  Solid over the biting end of the crown, gone by the neck.
                  The hold at 0.55 keeps the cap at full strength across the
                  part of the crown a person actually looks at, so the taper
                  reads as an ending rather than as a cap drawn too faintly.
                */}
                <stop offset="0" stopColor="var(--c-mask-opaque)" stopOpacity="1" />
                <stop offset="0.55" stopColor="var(--c-mask-opaque)" stopOpacity="1" />
                <stop offset="1" stopColor="var(--c-mask-opaque)" stopOpacity="0" />
              </linearGradient>
              <mask
                id={capMaskId}
                maskUnits="userSpaceOnUse"
                x="-6"
                y="-6"
                width={shape.width + 12}
                height={shape.height + 12}
              >
                <rect
                  x={-6}
                  y={-6}
                  width={shape.width + 12}
                  height={shape.height + 12}
                  fill={`url(#${capFadeId})`}
                />
              </mask>
              {/*
                The mask is on the GROUP and the scale on the path inside
                it, deliberately. Put both on one element and the gradient
                is scaled too, so the fade stops lining up with the neck.
              */}
              <g mask={`url(#${capMaskId})`}>
                <path className={styles.crownCap} d={shape.path} transform={capTransform} />
              </g>
            </>
          )}
        </svg>
        {isUpper && surfaceSlot}
        <span className={styles.numeral}>{fdiNumber}</span>
      </button>
    );
  };

  /**
   * One row of sixteen columns. A `null` holds its column empty — the outer
   * tier has nothing above the adult molars — and takes the column width of
   * the adult tooth at that position, so the tiers stay aligned.
   */
  const renderRow = (
    teeth: readonly (number | null)[],
    adultRow: readonly number[],
    isUpper: boolean,
    tier?: "inner" | "outer",
  ): React.JSX.Element => (
    <div className={`${styles.row} ${isUpper ? styles.upper : styles.lower}`} data-tier={tier}>
      {teeth.map((fdiNumber, index) =>
        fdiNumber === null ? (
          <span
            key={`gap-${String(index)}`}
            className={styles.gap}
            style={
              { "--tooth-col": `${String(columnWidth(adultRow[index] ?? 0))}px` } as CSSProperties
            }
            aria-hidden="true"
          />
        ) : (
          renderTooth(fdiNumber, isUpper)
        ),
      )}
    </div>
  );

  /**
   * One arch. With baby teeth on (BT1–BT8) it is two tiers, the adult teeth
   * still to come furthest from the bite — above the upper arch, below the
   * lower — which is where they sit in the jaw.
   */
  const renderArch = (adultRow: readonly number[], isUpper: boolean): React.JSX.Element => {
    const label = isUpper ? "Upper arch" : "Lower arch";
    const testId = isUpper ? "arch-upper" : "arch-lower";

    if (!primaryTeeth) {
      return (
        <div role="group" aria-label={label} data-testid={testId}>
          {renderRow(adultRow, adultRow, isUpper)}
        </div>
      );
    }

    const { inner, outer } = tiersOf(adultRow, states);
    const outerRow = renderRow(outer, adultRow, isUpper, "outer");
    const innerRow = renderRow(inner, adultRow, isUpper, "inner");

    return (
      <div role="group" aria-label={label} data-testid={testId} className={styles.tiers}>
        {isUpper ? outerRow : innerRow}
        {isUpper ? innerRow : outerRow}
      </div>
    );
  };

  return (
    <div
      ref={scale.chartRef}
      className={fit ? `${styles.chart} ${styles.fit}` : styles.chart}
      style={fit ? scale.style : undefined}
      data-testid="tooth-chart"
    >
      <ChartDefs />

      {/*
        **The whole drawing scales together** (PF7): the rows are zoomed as one,
        so no tooth changes shape, state or colour relative to another — it is
        the same chart, larger, and every tooth a bigger target.
      */}
      <div className={styles.rowsBox} ref={scale.boxRef}>
        <div className={styles.rows} ref={scale.rowsRef}>
          {renderArch(TEETH_UPPER, true)}
          <div className={styles.midline} />
          {renderArch(TEETH_LOWER, false)}
        </div>
      </div>

      <div className={styles.legend} ref={scale.legendRef}>
        <div>
          <div className={styles.legendTitle}>Condition</div>
          <div className={styles.legendItems}>
            {LEGEND.map(({ state, label }) => (
              <span key={state} className={styles.legendItem}>
                {/*
                  The swatch reuses the tooth's own state class, so the legend
                  cannot drift from what the chart draws — a legend maintained
                  separately is a legend that eventually lies.
                */}
                <span
                  className={`${styles.swatch} ${STATE_CLASS[state]}`}
                  data-testid="legend-swatch"
                  data-state={state}
                />
                {state === "MISSING" && primaryTeeth ? MISSING_WITH_PRIMARY : label}
              </span>
            ))}
            {surfaces !== undefined && (
              <span className={styles.legendItem}>
                {/* The real square, so the key cannot drift from the chart. */}
                <SurfaceSquare fdiNumber={16} worked={["O"]} className={styles.surfaceSwatch} />
                Part worked on
              </span>
            )}
            {surfaces !== undefined && (
              <span className={styles.legendItem}>
                <SurfaceSquare fdiNumber={16} planned={["O"]} className={styles.surfaceSwatch} />
                Part to be done
              </span>
            )}
            {primaryTeeth && (
              <span className={styles.legendItem}>
                {/* A faded sound swatch, by the same class as the tooth (BT6). */}
                <span
                  className={`${styles.swatch} ${styles.waiting}`}
                  data-testid="legend-swatch"
                  data-state="WAITING"
                />
                Not come in yet
              </span>
            )}
          </div>
          {/*
            Not in the patient file (PF7) — the client asked for it gone there;
            the booking form's picker keeps it.
          */}
          {!fit && (
            <div className={styles.legendNote}>
              Shape follows the tooth type — incisor, canine, premolar, molar.
            </div>
          )}
        </div>

        {/*
          The switch (BT8), beside the legend because it changes what the chart
          draws. Its state is the patient's — the parent saves it and passes it
          back — so it is controlled, never toggled locally.
        */}
        {(actions !== undefined || onPrimaryTeethChange !== undefined) && (
          <div className={styles.legendEnd}>
            {actions}
            {onPrimaryTeethChange !== undefined && (
              <label className={styles.primarySwitch}>
                <input
                  type="checkbox"
                  checked={primaryTeeth}
                  onChange={(event) => onPrimaryTeethChange(event.target.checked)}
                  data-testid="primary-teeth-toggle"
                />
                <span>Baby teeth</span>
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * **An adult tooth slides into the place of the baby tooth it replaced**
 * (BT10), rather than appearing there.
 *
 * Every tooth's box is remembered after each render, and when the set of
 * replaced teeth changes, a tooth that moved is drawn back where it was and
 * eased to where it is. It runs only on that change, so a resize or an ordinary
 * redraw never sets the chart moving.
 *
 * Distances are divided by the rows' zoom (PF7): a box is measured on screen,
 * but a transform inside the zoomed rows is in their own, unzoomed pixels.
 */
function useSlideIntoPlace(
  states: Partial<Record<number, ChartToothStateName>>,
  primaryTeeth: boolean,
  rowsRef: React.RefObject<HTMLDivElement | null>,
): React.RefObject<Map<number, HTMLButtonElement>> {
  const toothRefs = useRef(new Map<number, HTMLButtonElement>());
  const last = useRef<{ key: string; boxes: Map<number, DOMRect> } | null>(null);

  // Which adult teeth stand in a baby tooth's place: the only thing that moves one.
  const key = primaryTeeth
    ? [...TEETH_UPPER, ...TEETH_LOWER]
        .filter((fdi) => fdi % 10 <= 5 && states[fdi + 40] === "MISSING")
        .join(",")
    : "off";

  useLayoutEffect(() => {
    const boxes = new Map<number, DOMRect>();
    for (const [fdi, element] of toothRefs.current) {
      boxes.set(fdi, element.getBoundingClientRect());
    }

    const before = last.current;
    last.current = { key, boxes };
    // Switching the layer on or off redraws the whole chart; nothing slides.
    if (before === null || before.key === key || key === "off" || before.key === "off") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true) return;

    const rows = rowsRef.current;
    const zoom = rows === null ? 1 : Number.parseFloat(getComputedStyle(rows).zoom) || 1;
    for (const [fdi, element] of toothRefs.current) {
      const was = before.boxes.get(fdi);
      const now = boxes.get(fdi);
      if (was === undefined || now === undefined) continue;
      const dx = (was.left - now.left) / zoom;
      const dy = (was.top - now.top) / zoom;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
      element.animate?.(
        [{ transform: `translate(${String(dx)}px, ${String(dy)}px)` }, { transform: "none" }],
        { duration: 600, easing: "cubic-bezier(0.3, 0.7, 0.2, 1)" },
      );
    }
  });

  return toothRefs;
}

/**
 * The zoom that makes the rows fill the space the chart has (PF7, PF13).
 *
 * The rows' natural size is measured at zoom 1; after that the box they sit in
 * is watched and the zoom is the largest that fits it both ways, between 0.7
 * and 2 (`teethScale`). The box is sized by the layout, never by the rows — it
 * takes what the card leaves and cannot shrink below the rows at 0.7 — so the
 * zoom it produces cannot feed back into the size it is measured from.
 *
 * **The measurement belongs to one layout.** `layout` names what the rows are
 * drawing — the adult chart, or the chart with the baby-teeth tiers — and a
 * measurement taken for another layout is thrown away and taken again. Measured
 * once and kept, the two-row height survived the baby teeth being switched on
 * (or the patient's setting arriving after the first draw), so the box held
 * 129px while the four rows needed 249 and the lower arch spilled over the
 * legend and out of the card.
 *
 * **It also reports what the chart needs** (PF13), so the patient file can give
 * the teeth their room before the cards under them take any. The card's other
 * parts are added up from their own styles rather than read off the card,
 * because a card squeezed below its content would report a squeezed number.
 */
function useFitScale(
  fit: boolean,
  layout: string,
  onMeasure: ((needs: ChartNeeds) => void) | undefined,
): {
  chartRef: React.RefObject<HTMLDivElement | null>;
  boxRef: React.RefObject<HTMLDivElement | null>;
  rowsRef: React.RefObject<HTMLDivElement | null>;
  legendRef: React.RefObject<HTMLDivElement | null>;
  style: CSSProperties;
} {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const rowsRef = useRef<HTMLDivElement | null>(null);
  const legendRef = useRef<HTMLDivElement | null>(null);
  const [natural, setNatural] = useState<{
    layout: string;
    width: number;
    height: number;
  } | null>(null);
  const [scale, setScale] = useState(1);

  // The callback changes identity every render of the parent; the observer
  // below must not be torn down and rebuilt for that.
  const report = useRef(onMeasure);
  report.current = onMeasure;
  const reported = useRef("");

  // Only a measurement of what is drawn now counts.
  const measured = natural !== null && natural.layout === layout ? natural : null;

  useLayoutEffect(() => {
    if (!fit) return;
    const chart = chartRef.current;
    const box = boxRef.current;
    const rows = rowsRef.current;
    const legend = legendRef.current;
    if (chart === null || box === null || rows === null || legend === null) return;

    // Zoom 1 until measured (see `style` below), so the measurement is the
    // natural size.
    if (measured === null) {
      setNatural({ layout, width: rows.offsetWidth, height: rows.offsetHeight });
      return;
    }

    const update = (): void => {
      setScale(
        teethScale({
          box: { width: box.clientWidth, height: box.clientHeight },
          natural: measured,
        }),
      );

      const card = getComputedStyle(chart);
      const px = (value: string): number => Number.parseFloat(value) || 0;
      const chrome = Math.ceil(
        px(card.paddingTop) +
          px(card.paddingBottom) +
          px(card.borderTopWidth) +
          px(card.borderBottomWidth) +
          px(card.rowGap) +
          // Drawn at the legend's own zoom, which a bounding box includes.
          legend.getBoundingClientRect().height,
      );
      const chromeX = Math.ceil(
        px(card.paddingLeft) +
          px(card.paddingRight) +
          px(card.borderLeftWidth) +
          px(card.borderRightWidth),
      );
      const needs = { rowsHeight: measured.height, rowsWidth: measured.width, chrome, chromeX };
      const key = [needs.rowsHeight, needs.rowsWidth, needs.chrome, needs.chromeX].join(":");
      if (key !== reported.current) {
        reported.current = key;
        report.current?.(needs);
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(box);
    observer.observe(legend);
    return () => observer.disconnect();
  }, [fit, layout, measured]);

  return {
    chartRef,
    boxRef,
    rowsRef,
    legendRef,
    style: {
      "--teeth-scale": String(measured === null ? 1 : scale),
      ...(measured === null
        ? {}
        : { "--teeth-min-height": `${String(Math.ceil(measured.height * MIN_TEETH_SCALE))}px` }),
    } as CSSProperties,
  };
}
